import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useCopyResolver } from "@/hooks/useSiteCopy";
import { STAT_COPY_KEY, toSiteStats, visibleStats } from "@/lib/siteStats";
import { cn } from "@/lib/utils";

// Three real numbers on the home, owner and collection pages (backlog
// b29bf95c). Each shows only once it is believable (STAT_MIN); with none
// believable the bar is not rendered at all.
export const siteStatsQueryKey = (tenantId: string) => ["site-stats", tenantId] as const;

export function useSiteStats() {
  const tenant = useTenant();
  return useQuery({
    queryKey: siteStatsQueryKey(tenant.id),
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("public_site_stats", { p_tenant: tenant.id });
      return error ? null : toSiteStats(data);
    },
  });
}

const StatBar = ({ className }: { className?: string }) => {
  const { data } = useSiteStats();
  const copy = useCopyResolver();
  const items = visibleStats(data);
  if (items.length === 0) return null;
  return (
    <dl className={cn("flex flex-wrap justify-center gap-x-10 gap-y-4 text-center", className)}>
      {items.map((i) => (
        <div key={i.key} className="min-w-0 max-w-[12rem]">
          <dt className="sr-only">{copy(STAT_COPY_KEY[i.key]).value}</dt>
          <dd className="font-serif text-3xl sm:text-4xl font-semibold tabular-nums text-foreground">{i.value}</dd>
          <dd className="mt-1 text-[13px] text-muted-foreground leading-snug [overflow-wrap:anywhere]" aria-hidden>
            {copy(STAT_COPY_KEY[i.key]).value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export default StatBar;
